// Period helpers for KPI measurement + report entry flows. All arithmetic
// is done in UTC — no `new Date()` without a UTC getter — so local
// timezone drift cannot shift a period key. `now` is an injectable
// parameter (defaulting to `new Date()`) for deterministic testing.

import type { Kpi, KpiMeasurement, KpiReport } from "../../types";
import { matchKpiFrequency } from "./kpiValueParsers";

export type KpiFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function normaliseFrequency(raw: string): KpiFrequency {
  // Default legacy / unrecognised KPIs to monthly — the commonest choice
  // in the dataset, and date arithmetic needs a concrete frequency.
  return matchKpiFrequency(raw) ?? "monthly";
}

// ─── Key generators ────────────────────────────────────────────────────

function isoWeekKey(date: Date): string {
  // ISO-8601 week number. The week containing Thursday of a given week
  // determines which ISO year it belongs to.
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const dayOfWeek = d.getUTCDay() || 7; // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayOfWeek); // nearest Thursday
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((d.getTime() - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${pad2(week)}`;
}

export function periodKeyFromDate(date: Date, frequency: KpiFrequency): string {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  switch (frequency) {
    case "daily":
      return `${y}-${pad2(m + 1)}-${pad2(d)}`;
    case "monthly":
      return `${y}-${pad2(m + 1)}`;
    case "quarterly": {
      const q = Math.floor(m / 3) + 1;
      return `${y}-Q${q}`;
    }
    case "annually":
      return String(y);
    case "weekly":
      return isoWeekKey(date);
    default:
      return String(y);
  }
}

// ─── Labels ────────────────────────────────────────────────────────────

const DAILY_RE = /^(\d{4})-(\d{2})-(\d{2})$/u;
const MONTHLY_RE = /^(\d{4})-(\d{2})$/u;
const QUARTERLY_RE = /^(\d{4})-Q([1-4])$/u;
const ANNUAL_RE = /^(\d{4})$/u;
const WEEKLY_RE = /^(\d{4})-W(\d{2})$/u;

export function formatPeriodLabel(
  period: string,
  frequency: KpiFrequency,
): string {
  switch (frequency) {
    case "daily": {
      const m = DAILY_RE.exec(period);
      if (!m) return period;
      const day = Number(m[3]);
      const month = MONTH_LABELS[Number(m[2]) - 1];
      return `${pad2(day)} ${month} ${m[1]}`;
    }
    case "monthly": {
      const m = MONTHLY_RE.exec(period);
      if (!m) return period;
      const month = MONTH_LABELS[Number(m[2]) - 1];
      return `${month} ${m[1]}`;
    }
    case "quarterly": {
      const m = QUARTERLY_RE.exec(period);
      if (!m) return period;
      return `Q${m[2]} ${m[1]}`;
    }
    case "annually": {
      return period;
    }
    case "weekly": {
      const m = WEEKLY_RE.exec(period);
      if (!m) return period;
      return `Week ${m[2]} of ${m[1]}`;
    }
    default:
      return period;
  }
}

// ─── Range enumeration ─────────────────────────────────────────────────

function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 86400000);
}

function startOfDayUTC(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
}

function normaliseRange(from: Date, to: Date): [Date, Date] {
  return from.getTime() <= to.getTime() ? [from, to] : [to, from];
}

export function periodRangeInclusive(
  fromDate: Date,
  toDate: Date,
  frequency: KpiFrequency,
): string[] {
  const [from, to] = normaliseRange(fromDate, toDate);
  const keys: string[] = [];
  switch (frequency) {
    case "daily": {
      let cursor = startOfDayUTC(from);
      const end = startOfDayUTC(to);
      while (cursor.getTime() <= end.getTime()) {
        keys.push(periodKeyFromDate(cursor, "daily"));
        cursor = addDays(cursor, 1);
      }
      break;
    }
    case "weekly": {
      let cursor = startOfDayUTC(from);
      const end = startOfDayUTC(to);
      const seen = new Set<string>();
      while (cursor.getTime() <= end.getTime()) {
        const k = isoWeekKey(cursor);
        if (!seen.has(k)) {
          seen.add(k);
          keys.push(k);
        }
        cursor = addDays(cursor, 1);
      }
      break;
    }
    case "monthly": {
      let y = from.getUTCFullYear();
      let m = from.getUTCMonth();
      const endY = to.getUTCFullYear();
      const endM = to.getUTCMonth();
      while (y < endY || (y === endY && m <= endM)) {
        keys.push(`${y}-${pad2(m + 1)}`);
        m += 1;
        if (m > 11) {
          m = 0;
          y += 1;
        }
      }
      break;
    }
    case "quarterly": {
      let y = from.getUTCFullYear();
      let q = Math.floor(from.getUTCMonth() / 3) + 1;
      const endY = to.getUTCFullYear();
      const endQ = Math.floor(to.getUTCMonth() / 3) + 1;
      while (y < endY || (y === endY && q <= endQ)) {
        keys.push(`${y}-Q${q}`);
        q += 1;
        if (q > 4) {
          q = 1;
          y += 1;
        }
      }
      break;
    }
    case "annually": {
      for (let y = from.getUTCFullYear(); y <= to.getUTCFullYear(); y += 1) {
        keys.push(String(y));
      }
      break;
    }
  }
  return keys;
}

// ─── Next unlogged ─────────────────────────────────────────────────────

// Advance a period key by one unit for its frequency. Returns a key that
// is strictly later than `key`.
function advancePeriod(key: string, frequency: KpiFrequency): string {
  switch (frequency) {
    case "daily": {
      const m = DAILY_RE.exec(key);
      if (!m) return key;
      const d = new Date(
        Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])),
      );
      return periodKeyFromDate(addDays(d, 1), "daily");
    }
    case "monthly": {
      const m = MONTHLY_RE.exec(key);
      if (!m) return key;
      let y = Number(m[1]);
      let month = Number(m[2]); // 1-based
      month += 1;
      if (month > 12) {
        month = 1;
        y += 1;
      }
      return `${y}-${pad2(month)}`;
    }
    case "quarterly": {
      const m = QUARTERLY_RE.exec(key);
      if (!m) return key;
      let y = Number(m[1]);
      let q = Number(m[2]);
      q += 1;
      if (q > 4) {
        q = 1;
        y += 1;
      }
      return `${y}-Q${q}`;
    }
    case "annually": {
      const m = ANNUAL_RE.exec(key);
      if (!m) return key;
      return String(Number(m[1]) + 1);
    }
    case "weekly": {
      const m = WEEKLY_RE.exec(key);
      if (!m) return key;
      // Find a date in that ISO week then add 7 days and re-derive the key.
      // Monday of an ISO week can be found by: Jan 4 of that ISO year
      // belongs to week 1; shift by (week − 1) × 7 days, then adjust to
      // Monday.
      const y = Number(m[1]);
      const w = Number(m[2]);
      const jan4 = new Date(Date.UTC(y, 0, 4));
      const jan4Day = jan4.getUTCDay() || 7;
      const weekOneMonday = addDays(jan4, 1 - jan4Day);
      const thisMonday = addDays(weekOneMonday, (w - 1) * 7);
      const nextMonday = addDays(thisMonday, 7);
      return isoWeekKey(nextMonday);
    }
    default:
      return key;
  }
}

const PERIOD_SHAPE_RES: Record<KpiFrequency, RegExp> = {
  daily: DAILY_RE,
  weekly: WEEKLY_RE,
  monthly: MONTHLY_RE,
  quarterly: QUARTERLY_RE,
  annually: ANNUAL_RE,
};

export function nextUnloggedPeriod(
  kpi: Kpi,
  measurements: KpiMeasurement[],
  reports: KpiReport[],
  now: Date = new Date(),
): string {
  const frequency = normaliseFrequency(kpi.measurementFrequency);
  const logged = new Set<string>();
  for (const m of measurements) {
    if (m.parentId === kpi.id) logged.add(m.period);
  }
  for (const r of reports) {
    if (r.parentId === kpi.id) logged.add(r.period);
  }

  // KPIs are logged in arrears, so the default continues from the most
  // recent logged period rather than jumping to today's period. Gaps
  // behind the latest logged period are deliberately never backfilled.
  // With no usable history, fall back to the current period. Zero-padded
  // period keys of the same shape order correctly as strings; keys of a
  // different shape (e.g. after a frequency change) are ignored here but
  // still count as logged in the skip loop below.
  const shape = PERIOD_SHAPE_RES[frequency];
  let latest: string | null = null;
  for (const key of logged) {
    if (shape.test(key) && (latest === null || key > latest)) latest = key;
  }

  let candidate =
    latest !== null
      ? advancePeriod(latest, frequency)
      : periodKeyFromDate(now, frequency);
  // Bounded to protect against malformed input producing an infinite loop.
  const MAX_ADVANCES = 5000;
  for (let i = 0; i < MAX_ADVANCES; i += 1) {
    if (!logged.has(candidate)) return candidate;
    candidate = advancePeriod(candidate, frequency);
  }
  return candidate;
}
